import { createFileRoute, Link } from "@tanstack/react-router";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { usePersona } from "@/lib/persona";
import { useEvents } from "@/lib/events";

export const Route = createFileRoute("/app/")({
  component: PersonaDashboard,
});

/**
 * Single `/app` index that branches on persona. Attendees see real data from
 * `GET /api/events` now (B-tier endpoint, post-schema-v0 deploy). Partner +
 * organizer remain stubs until their first endpoints land.
 */
function PersonaDashboard() {
  const persona = usePersona();
  const { user } = useAuth();

  if (persona.role === "attendee") {
    return <AttendeeDashboard />;
  }

  if (persona.role === "partner") {
    return (
      <div className="space-y-6">
        <header>
          <h1 className="font-serif text-3xl text-palm">Welcome back</h1>
          <p className="text-muted-foreground">{user?.email}</p>
        </header>
        <Link to={"/app/venue" as never} className="block">
          <Card className="cursor-pointer border-palm/30 transition-shadow hover:border-palm/60 hover:shadow-md">
            <CardHeader>
              <CardTitle>Your venue</CardTitle>
              <CardDescription>
                Register your venue, activate your exposure package, and post your festival events.
              </CardDescription>
            </CardHeader>
            <CardContent className="text-sm font-medium text-palm">Open venue console →</CardContent>
          </Card>
        </Link>
        <div className="grid gap-4 md:grid-cols-3">
          <KpiCard label="Offerings live" value="—" note="Post events from the venue console." />
          <KpiCard label="Orders this week" value="—" note="M1 reveals real data." />
          <KpiCard label="Pending payouts" value="—" note="M2 (Stripe Connect)." />
        </div>
      </div>
    );
  }

  // organizer
  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-serif text-3xl text-palm">Festival dashboard</h1>
        <p className="text-muted-foreground">Real-time KPIs (stubbed until M1).</p>
      </header>
      <div className="grid gap-4 md:grid-cols-4">
        <KpiCard label="Tickets sold" value="—" note="M1 reveals data." />
        <KpiCard label="Gross revenue" value="—" note="M1 reveals data." />
        <KpiCard label="Active partners" value="—" note="M1 reveals data." />
        <KpiCard label="Days to launch" value="—" note="Auto from event date." />
      </div>
    </div>
  );
}

function KpiCard({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <Card>
      <CardHeader>
        <CardDescription>{label}</CardDescription>
        <CardTitle className="text-3xl">{value}</CardTitle>
      </CardHeader>
      <CardContent className="text-xs text-muted-foreground">{note}</CardContent>
    </Card>
  );
}

function AttendeeDashboard() {
  const { data: events, isLoading, isError } = useEvents();

  return (
    <div className="container space-y-6 py-6">
      <header>
        <h1 className="font-serif text-3xl text-palm">Festival events</h1>
        <p className="text-muted-foreground">Oct 28 – Nov 27, 2026. Tulum.</p>
      </header>

      {isLoading && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Loading events…</CardTitle>
          </CardHeader>
        </Card>
      )}

      {isError && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Could not load events</CardTitle>
            <CardDescription>
              The API responded with an error. Try refreshing in a moment.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {!isLoading && !isError && events && events.length === 0 && (
        <Card>
          <CardHeader>
            <CardTitle>No events yet</CardTitle>
            <CardDescription>
              The schedule will fill out as partners are confirmed. Check back soon.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {events && events.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2">
          {events.map((ev) => (
            <Link
              key={ev.id}
              to={"/calendar" as never}
              className="block rounded-xl transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-palm"
            >
              <Card className="h-full cursor-pointer border-palm/20 hover:border-palm/50">
                <CardHeader>
                  <CardTitle className="text-xl">{ev.name}</CardTitle>
                  <CardDescription>
                    {ev.timezone}
                    {ev.days.length > 0 && (
                      <>
                        {" · "}
                        {ev.days[0].date}
                        {ev.days.length > 1 && ` → ${ev.days[ev.days.length - 1].date}`}
                      </>
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Browse the full calendar of experiences</span>
                  <span className="font-medium text-palm">Open calendar →</span>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
